import React, { useEffect, useState } from 'react';
import { CircleStackIcon } from '@heroicons/react/24/outline';
import { Badge, Button, Checkbox, Select, SelectOption, Spinner } from '../ui';
import {
  ConnectorType,
  type ConnectorConfiguration,
  useListConnectors,
} from '../../generated/connectors/connectors';
import {
  useGetConnectorRegistry,
  useUpdateWorkspaceTableScope
} from '../../hooks/useWarehouseRegistry';

export interface StructuredSourceAssetAllowlistSectionProps {
  workspaceId: number;
  connectorId?: number; // Optional if not yet configured
  selectedTableIds?: number[];
  onSaveScope?: (tableIds: number[], warehouseConnectorId?: number | null) => Promise<void>;
  onSuggestOpenRegistry: () => void;
}

const WAREHOUSE_CONNECTOR_TYPES = new Set<string>([
  ConnectorType.BIGQUERY,
  ConnectorType.ERP,
  ConnectorType.MARIADB,
  ConnectorType.POSTGRES,
  ConnectorType.SNOWFLAKE,
]);

function connectorLabel(connector: ConnectorConfiguration): string {
  const provider =
    connector.connector_type === ConnectorType.BIGQUERY
      ? 'BigQuery'
      : connector.connector_type === ConnectorType.ERP
        ? 'ERP'
      : connector.connector_type === ConnectorType.MARIADB
        ? 'MariaDB'
      : connector.connector_type === ConnectorType.POSTGRES
        ? 'PostgreSQL'
        : 'Snowflake';
  return `${provider} - ${connector.connector_name}`;
}

export function StructuredSourceAssetAllowlistSection({
  workspaceId,
  connectorId,
  selectedTableIds = [],
  onSaveScope,
  onSuggestOpenRegistry,
}: StructuredSourceAssetAllowlistSectionProps) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [selectedConnectorId, setSelectedConnectorId] = useState<string>(connectorId ? String(connectorId) : '');
  const selectedTableIdsKey = selectedTableIds.join(',');
  const activeConnectorId = selectedConnectorId || (connectorId ? String(connectorId) : '');

  const { data: projects, isLoading } = useGetConnectorRegistry(activeConnectorId || 0);
  const updateScope = useUpdateWorkspaceTableScope(workspaceId);
  const { data: connectorsData, isLoading: connectorsLoading } = useListConnectors({ is_enabled: true });

  const warehouseConnectors = React.useMemo(
    () =>
      (connectorsData?.connectors || []).filter((connector) =>
        WAREHOUSE_CONNECTOR_TYPES.has(String(connector.connector_type)),
      ),
    [connectorsData?.connectors],
  );

  useEffect(() => {
    setSelectedIds(new Set(selectedTableIds));
  }, [selectedTableIds, selectedTableIdsKey]);

  useEffect(() => {
    setSelectedConnectorId(connectorId ? String(connectorId) : '');
  }, [connectorId]);

  useEffect(() => {
    if (selectedConnectorId && connectorId && selectedConnectorId !== String(connectorId)) {
      setSelectedIds(new Set());
    }
  }, [connectorId, selectedConnectorId]);

  // Flatten tables for easier listing
  const allTables = React.useMemo(() => {
    return projects?.flatMap(p =>
      p.datasets.flatMap(d =>
        d.tables.map(t => ({
          ...t,
          datasetName: d.name || d.dataset_external_id,
          projectName: p.name || p.project_external_id,
        }))
      )
    ) || [];
  }, [projects]);

  // Initial selection (this should ideally come from a workspace-scope query,
  // but for now we'll assume the registry tables might have an 'is_scoped' flag or similar,
  // or we just use the selection state).
  // Actually, I should probably add a useGetWorkspaceScope hook.

  const toggle = (id: number, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const tableIds = Array.from(selectedIds);
      const selectedConnector = selectedConnectorId ? Number(selectedConnectorId) : null;
      if (onSaveScope) {
        await onSaveScope(tableIds, selectedConnector);
      } else {
        await updateScope.mutateAsync({ table_ids: tableIds });
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!connectorId) {
    return (
      <section className="mb-6 rounded-lg border border-dashed border-gray-300 bg-white p-6 dark:border-dark-border dark:bg-dark-surface">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
              <CircleStackIcon className="h-5 w-5 text-eliza-red" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                Structured warehouse scope
              </div>
              <h3 className="mt-1 text-sm font-semibold text-charcoal dark:text-white">
                Select a structured source
              </h3>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
                Tenant structured connections live in Data Connections. Pick one here to make its governed tables available to this workspace.
              </p>
            </div>
          </div>
          <Button variant="secondary" onClick={onSuggestOpenRegistry}>
            Open Data Connections
          </Button>
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(280px,420px)_auto] lg:items-end">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Structured connection
            </label>
            <Select
              value={selectedConnectorId}
              onValueChange={setSelectedConnectorId}
              placeholder={connectorsLoading ? 'Loading structured connections...' : 'Choose a structured database or ERP source'}
              disabled={connectorsLoading || warehouseConnectors.length === 0}
              className="w-full"
            >
              {warehouseConnectors.map((connector) => (
                <SelectOption key={connector.id} value={String(connector.id)}>
                  {connectorLabel(connector)}
                </SelectOption>
              ))}
            </Select>
          </div>
          <Button
            variant="brand"
            onClick={handleSave}
            disabled={!selectedConnectorId || isSaving}
            className="gap-2"
          >
            {isSaving && <Spinner size="xs" className="text-current" />}
            Link Warehouse
          </Button>
        </div>

        {!connectorsLoading && warehouseConnectors.length === 0 && (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            No active structured connections found. Create or sync a structured database or ERP source in Data Connections first.
          </p>
        )}
      </section>
    );
  }

  return (
    <section
      id="tts-allowlist"
      className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-[hsl(220_25%_18%)] dark:bg-[hsl(217_33%_12%)]"
    >
      <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 dark:border-dark-border lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface-2">
            <CircleStackIcon className="h-5 w-5 text-eliza-red" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Structured source scope
            </div>
            <h3 className="mt-1 text-sm font-semibold text-charcoal dark:text-white">
              W3 • Asset allowlist
            </h3>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">
              Pick structured tables to include in this workspace's knowledge graph.
              These tables will be used by Eliza to answer natural language questions.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={selectedConnectorId}
            onValueChange={setSelectedConnectorId}
            placeholder="Structured connection"
            disabled={connectorsLoading || warehouseConnectors.length === 0}
            className="min-w-[260px]"
          >
            {warehouseConnectors.map((connector) => (
              <SelectOption key={connector.id} value={String(connector.id)}>
                {connectorLabel(connector)}
              </SelectOption>
            ))}
          </Select>
          <Badge variant="secondary">{selectedIds.size} selected</Badge>
          <Button
            variant="brand"
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !selectedConnectorId || allTables.length === 0}
            className="gap-2"
          >
            {isSaving && <Spinner size="xs" className="text-current" />}
            Save Scope
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Spinner size="md" />
          <span className="ml-2 text-sm text-gray-500">Fetching registry...</span>
        </div>
      ) : allTables.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          No structured registry assets synced yet. Open Data Connections to sync metadata.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-400">
                <th className="px-4 py-3 w-14 text-center">Include</th>
                <th className="px-5 py-3">Project</th>
                <th className="px-5 py-3">Dataset</th>
                <th className="px-5 py-3">Table Name</th>
                <th className="px-5 py-3">Row Count</th>
                <th className="px-5 py-3 text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {allTables.map((row) => (
                <tr key={row.id} data-testid={`allowlist-row-${row.id}`} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2">
                  <td className="px-4 py-3 text-center">
                    <Checkbox
                      aria-label={`Include ${row.name}`}
                      checked={selectedIds.has(row.id)}
                      onChange={(e) => toggle(row.id, e.target.checked)}
                    />
                  </td>
                  <td className="px-5 py-3 text-charcoal dark:text-gray-100">
                    {row.projectName}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-gray-500">
                    {row.datasetName}
                  </td>
                  <td className="px-5 py-3 font-medium text-charcoal dark:text-gray-100">
                    {row.name || row.table_external_id}
                  </td>
                  <td className="px-5 py-3 text-gray-600 dark:text-gray-400">
                    {row.row_count_snapshot?.toLocaleString() || '—'}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {row.has_drift ? (
                      <Badge variant="danger">Drift</Badge>
                    ) : (
                      <Badge variant="success">Healthy</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
